/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TablePagination from "@mui/material/TablePagination";
import TableSortLabel from "@mui/material/TableSortLabel";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ExcelExportButton from "./export";

function formatNumber(num) {
  const n = Number(num);
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// ---- sorting helpers ----
function getComparator(order = "asc", orderBy, headers) {
  const col = (headers || []).find((h) => h.id === orderBy);

  // allow custom sortValue (recommended when render returns JSX)
  const pick = (row) => {
    if (!col) return row?.[orderBy];

    if (typeof col.sortValue === "function") return col.sortValue(row);

    // fallback: use raw field
    return row?.[col.id];
  };

  return (a, b) => {
    const av = pick(a);
    const bv = pick(b);

    // handle nullish
    if (av == null && bv == null) return 0;
    if (av == null) return order === "asc" ? -1 : 1;
    if (bv == null) return order === "asc" ? 1 : -1;

    // numbers
    const an = Number(av);
    const bn = Number(bv);
    const bothNumbers = Number.isFinite(an) && Number.isFinite(bn);

    let cmp = 0;

    if (bothNumbers) {
      cmp = an < bn ? -1 : an > bn ? 1 : 0;
    } else {
      // string compare
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      cmp = as < bs ? -1 : as > bs ? 1 : 0;
    }

    return order === "asc" ? cmp : -cmp;
  };
}

function stableSort(array, comparator) {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}

function DenseTable({
  headers = [],
  rows = [],
  height = 520,
  filename = "export",
  component,
  tableSpans,
  sx,
  rowKey = "id",

  // ✅ new props
  loading = false,
  emptyText = "No data found",
  onRowClick, // (row) => void
  getRowSx, // (row) => sx object
  initialSortBy, // e.g. "createdAt"
  initialSortDirection = "asc", // "asc" | "desc"
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // ✅ sorting state
  const [sortBy, setSortBy] = useState(initialSortBy || "");
  const [sortDirection, setSortDirection] = useState(initialSortDirection);

  // ✅ reset pagination when data changes
  useEffect(() => {
    setPage(0);
  }, [rowsPerPage, rows.length]);

  // ✅ ensure sortBy is valid when headers change
  useEffect(() => {
    if (!sortBy) return;
    const exists = headers.some((h) => h.id === sortBy);
    if (!exists) setSortBy("");
  }, [headers, sortBy]);

  const sortedRows = useMemo(() => {
    if (!sortBy) return rows;
    const comparator = getComparator(sortDirection, sortBy, headers);
    return stableSort(rows, comparator);
  }, [rows, sortBy, sortDirection, headers]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  const handleRequestSort = (header) => {
    if (!header?.id || header.disableSort) return;

    // if clicking same column, flip direction
    if (sortBy === header.id) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }

    setSortBy(header.id);
    setSortDirection("asc");
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isEmpty = !loading && (rows?.length || 0) === 0;

  const headerCellStyle = {
    backgroundColor: "#fff",
    fontWeight: 600,
    fontSize: "12px",
    textTransform: "capitalize",
    padding: 1,
    paddingLeft: 5,
    fontFamily: "Inter, Roboto, sans-serif",
    letterSpacing: "0.03em",
    border: "1px solid #e0e0e0",
    whiteSpace: "nowrap",
  };

  const bodyCellStyle = {
    fontFamily: "Inter, Roboto, sans-serif",
    letterSpacing: "0.03em",
    fontSize: "12px",
    padding: 1,
    paddingLeft: 5,
    fontWeight: 400,
    lineHeight: 1.5,
    margin: 0,
    border: "1px solid #e0e0e0",
    color: "#222222",
    whiteSpace: "nowrap",
  };

  return (
    <Paper>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
          alignItems: "center",
          px: 2,
          py: 1,
        }}
      >
        <ExcelExportButton rows={rows} columns={headers} filename={filename} />
        <Box>{component}</Box>
      </Box>

      <TableContainer style={{ maxHeight: height }}>
        <Table stickyHeader sx={sx} size="small" aria-label="dense table">
          <TableHead
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 2,
            }}
          >
            {tableSpans}

            <TableRow style={{ backgroundColor: "#fff" }}>
              {headers.map((header, index) => {
                const active = sortBy === header.id;
                const sortable = !!header.id && !header.disableSort;

                return (
                  <TableCell
                    key={header.id || index}
                    style={{
                      ...headerCellStyle,
                      minWidth: header.width,
                      maxWidth: header.width,
                    }}
                    align={header.align || "left"}
                    onClick={() => sortable && handleRequestSort(header)}
                  >
                    {sortable ? (
                      <TableSortLabel
                        active={active}
                        direction={active ? sortDirection : "asc"}
                      >
                        {header.label}
                      </TableSortLabel>
                    ) : (
                      header.label
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {/* ✅ Loading */}
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={headers.length}
                  align="center"
                  sx={{ py: 3 }}
                >
                  <Typography variant="body2">Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : null}

            {/* ✅ Empty */}
            {isEmpty ? (
              <TableRow>
                <TableCell
                  colSpan={headers.length}
                  align="center"
                  sx={{ py: 3 }}
                >
                  <Typography variant="body2">{emptyText}</Typography>
                </TableCell>
              </TableRow>
            ) : null}

            {/* ✅ Data */}
            {!loading &&
              !isEmpty &&
              paginatedRows.map((row, rIndex) => (
                <TableRow
                  key={row?.[rowKey] ?? rIndex}
                  hover
                  onClick={() => onRowClick?.(row)}
                  sx={{
                    cursor: onRowClick ? "pointer" : "default",
                    ...(typeof getRowSx === "function" ? getRowSx(row) : null),
                  }}
                >
                  {headers.map((header, colIndex) => {
                    const raw = row?.[header.id];

                    const display = header.render
                      ? header.render(row)
                      : typeof header.format === "function"
                        ? header.format(raw, row)
                        : header.isNumber
                          ? formatNumber(raw)
                          : typeof raw === "number"
                            ? formatNumber(raw) // fallback behavior
                            : (raw ?? "");

                    return (
                      <TableCell
                        key={header.id || colIndex}
                        align={header.align || "left"}
                        style={bodyCellStyle}
                      >
                        {display}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
}

export default React.memo(DenseTable);
